import { Link } from "react-router-dom";
import { Ruler, Tag as TagIcon, ExternalLink, MessageCircle } from "lucide-react";
import { productEnquiryMessage } from "@/utilities/whatsapp";
import { buildWhatsAppUrl } from "@/utilities/whatsapp";
import { trackEvent, AnalyticsEvents } from "@/utilities/analytics";
import { AVAILABILITY_OPTIONS } from "@/utilities/filters";
import SmartImage from "@/components/common/SmartImage";

const conditionLabel = {
    new: "New",
    used: "Used",
    refurbished: "Refurbished",
    "custom-build": "Custom Build",
};

function BadgeCluster({ product }) {
    const badges = [];
    if (product.featured) badges.push({ cls: "cs-badge-featured", text: "Featured" });
    if (product.condition === "new") badges.push({ cls: "cs-badge-new", text: "New" });
    if (product.condition === "used") badges.push({ cls: "cs-badge-used", text: "Used" });
    if (product.condition === "custom-build") badges.push({ cls: "cs-badge-custom", text: "Custom Build" });
    if (product.newListing) badges.push({ cls: "cs-badge-new", text: "New Listing" });
    return (
        <div className="absolute top-3 left-3 flex flex-col gap-1.5" aria-hidden="true">
            {badges.map((b) => (
                <span key={b.text} className={b.cls}>
                    {b.text}
                </span>
            ))}
        </div>
    );
}

export default function ProductCard({ product, layout = "grid" }) {
    const availability = AVAILABILITY_OPTIONS.find((a) => a.value === product.availability) || {
        label: "Enquire for Availability",
    };
    const enquireUrl = buildWhatsAppUrl(productEnquiryMessage(product));
    const detailHref = `/containers/${product.slug}`;
    const specHighlights = product.specifications?.slice(0, 4) || [];

    return (
        <article
            className={`cs-card cs-card-hover group flex flex-col overflow-hidden ${layout === "row" ? "md:flex-row" : ""}`}
            data-testid={`product-card-${product.slug}`}
        >
            <Link
                to={detailHref}
                aria-label={`View details of ${product.name}`}
                className={`relative block overflow-hidden bg-cs-charcoal ${layout === "row" ? "md:w-2/5" : ""}`}
                onClick={() =>
                    trackEvent(AnalyticsEvents.PRODUCT_VIEW, {
                        productId: product.id,
                        productName: product.name,
                        category: product.categoryName,
                    })
                }
                data-testid={`product-card-image-link-${product.slug}`}
            >
                {/* Centre anchor: these are photographs, where the container sits
                    mid-frame. The top anchor used for blog artwork would favour
                    sky over subject. */}
                <SmartImage
                    src={product.featuredImage}
                    alt={`${product.name} — ${conditionLabel[product.condition]} ${product.size} container in Singapore`}
                    width={800}
                    position="center"
                    className="aspect-[4/3] w-full"
                    imgClassName="transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                />
                <BadgeCluster product={product} />
                <div className="absolute bottom-3 right-3 cs-chip !bg-white/95 !border-cs-navy !text-cs-navy">
                    <TagIcon size={11} strokeWidth={2} />
                    {product.categoryName}
                </div>
            </Link>

            <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center justify-between gap-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-cs-steel">
                        Ref: {product.referenceNumber}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cs-orange">
                        <span className="inline-block w-1.5 h-1.5 bg-cs-orange mr-1.5 align-middle cs-pulse-dot" />
                        {availability.label}
                    </p>
                </div>
                <h3 className="mt-2 font-heading uppercase text-xl md:text-[22px] leading-tight text-cs-navy">
                    <Link
                        to={detailHref}
                        className="hover:text-cs-orange transition-colors"
                        data-testid={`product-card-title-${product.slug}`}
                    >
                        {product.name}
                    </Link>
                </h3>
                <p className="mt-2 text-sm text-cs-steel line-clamp-3">
                    {product.shortDescription}
                </p>

                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-cs-steel/20 pt-3 font-mono text-[11px] uppercase tracking-widest text-cs-steel">
                    {specHighlights.map((s) => (
                        <div key={s.label} className="flex items-center justify-between gap-2 py-1">
                            <dt className="text-cs-steel/80">{s.label}</dt>
                            <dd className="text-cs-navy truncate">{s.value}</dd>
                        </div>
                    ))}
                </dl>

                <div className="mt-4 flex items-center justify-between border-t border-cs-steel/20 pt-3">
                    <div className="flex items-center gap-2 text-cs-navy font-mono text-xs uppercase tracking-widest">
                        <Ruler size={14} strokeWidth={2} />
                        {product.size}
                    </div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-cs-orange">
                        Contact for Price
                    </p>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2">
                    <Link
                        to={detailHref}
                        className="cs-btn-outline-dark text-[11px] py-2.5"
                        data-testid={`product-card-view-details-${product.slug}`}
                    >
                        <ExternalLink size={14} strokeWidth={2} />
                        View Details
                    </Link>
                    <a
                        href={enquireUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                            trackEvent(AnalyticsEvents.PRODUCT_ENQUIRY_CLICK, {
                                productId: product.id,
                                productName: product.name,
                                source: "product-card",
                            })
                        }
                        className="cs-btn-whatsapp text-[11px] py-2.5"
                        data-testid={`product-card-whatsapp-${product.slug}`}
                    >
                        <MessageCircle size={14} strokeWidth={2} />
                        Enquire
                    </a>
                </div>
            </div>
        </article>
    );
}
